// Signed-in user context. Loads GET /me once for the whole app (topbar avatar,
// home group list, profile page) and applies the account language. Pages that
// need fresher data call reload().

import type { ReactElement, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getMe, type Me } from './api';
import { applyAccountLanguage } from './i18n';
import { wordingFor, type Wording } from './projectType';

type UserContextValue = {
  me: Me | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

const UserContext = createContext<UserContextValue>({
  me: null,
  loading: true,
  error: null,
  reload: () => {},
});

export function UserProvider({ children }: { children: ReactNode }): ReactElement {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getMe()
      .then((m) => {
        if (!alive) return;
        setMe(m);
        applyAccountLanguage(m.language);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  const value = useMemo(
    () => ({ me, loading, error, reload }),
    [me, loading, error, reload],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  return useContext(UserContext);
}

// How project `pid` words its groups and categories (wordingFor). Every /me
// entry of a project carries the same type.
export function useWording(pid: string | undefined): Wording {
  const { me } = useUser();
  return wordingFor(me?.categories_and_groups.find((g) => g.project_pid === pid)?.project_type);
}
